import { useTranslation } from 'react-i18next';
import { Box, SideDrawer, withStyles, type AccountUserRole } from 'vopay';
import { Button } from '#shared/components/UI';
import styles from './RoleDetailsDrawer.module.scss';

const cn = withStyles(styles);

type RoleDetailsDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  usersCount: number;
  isFetchingUsers: boolean;
  hasSubAccounts: boolean;
  canManage: boolean;
  onClose: () => void;
  /** Without it there is no Edit Role button. */
  onEdit?: () => void;
  onDelete: () => void;
  onViewPermissions: () => void;
  onViewUsers: () => void;
};

export default function RoleDetailsDrawer({
  isOpen,
  role,
  usersCount,
  isFetchingUsers,
  hasSubAccounts,
  canManage,
  onClose,
  onEdit,
  onDelete,
  onViewPermissions,
  onViewUsers,
}: RoleDetailsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.RoleDetailsDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });

  if (!role) return null;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Role Details' })}
      footer={
        canManage ? (
          <Box className={cn('footer')}>
            <Button colorScheme="secondary" onClick={onDelete}>
              {t('deleteRole', { defaultValue: 'Delete Role' })}
            </Button>

            {onEdit && (
              <Button colorScheme="primary" onClick={onEdit}>
                {t('editRole', { defaultValue: 'Edit Role' })}
              </Button>
            )}
          </Box>
        ) : undefined
      }
    >
      <div className={cn('body')}>
        <div className={cn('detailsCard')}>
          <div className={cn('row')}>
            <span className={cn('label')}>{t('roleName', { defaultValue: 'Role name' })}</span>
            <span className={cn('value')}>{role.Name}</span>
          </div>

          <div className={cn('row')}>
            <span className={cn('label')}>{t('permissionsEnabled', { defaultValue: 'Permissions enabled' })}</span>

            <button type="button" className={cn('valueClickable')} onClick={onViewPermissions}>
              {role.Permissions.length}
            </button>
          </div>

          {hasSubAccounts && (
            <div className={cn('row')}>
              <span className={cn('label')}>
                {t('appliesToSubaccounts', { defaultValue: 'Applies to sub-accounts' })}
              </span>

              <span className={cn('value')}>
                {role.ApplyToAllSubaccounts
                  ? tButtons('yes', { defaultValue: 'Yes' })
                  : tButtons('no', { defaultValue: 'No' })}
              </span>
            </div>
          )}

          <div className={cn('row')}>
            <span className={cn('label')}>{t('users', { defaultValue: 'Users' })}</span>

            <button
              type="button"
              className={cn('valueClickable')}
              disabled={isFetchingUsers || usersCount === 0}
              onClick={usersCount > 0 ? onViewUsers : undefined}
            >
              {isFetchingUsers || usersCount === 0 ? '—' : usersCount}
            </button>
          </div>
        </div>
      </div>
    </SideDrawer>
  );
}
